import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface CalculatorProps {
  open: boolean;
  onClose: () => void;
  onCalculate: (value: number) => void;
  initialValue?: number;
}

interface HistoryEntry {
  expression: string;
  result: number;
}

export default function Calculator({ open, onClose, onCalculate, initialValue = 0 }: CalculatorProps) {
  const [display, setDisplay] = useState<string>(initialValue.toString());
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForNewValue, setWaitingForNewValue] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [currentExpression, setCurrentExpression] = useState<string>('');
  const [fullExpression, setFullExpression] = useState<string>('');
  const [pendingCalculation, setPendingCalculation] = useState<{ expression: string; result: number } | null>(null);

  // Reset calculator state when dialog opens or initialValue changes
  useEffect(() => {
    if (open) {
      setDisplay(initialValue.toString());
      setPreviousValue(null);
      setOperation(null);
      setWaitingForNewValue(false);
      setCurrentExpression('');
      setFullExpression('');
      setPendingCalculation(null);
    }
  }, [open, initialValue]);

  // Use refs to store the latest handler functions
  const handlersRef = useRef<{
    handleNumber: (num: string) => void;
    handleDecimal: () => void;
    handleOperation: (op: string) => void;
    handleEquals: () => void;
    handleClear: () => void;
    handleBackspace: () => void;
    handleOpenParenthesis: () => void;
    handleCloseParenthesis: () => void;
  } | null>(null);

  const handleNumber = (num: string) => {
    // If there's a pending calculation, perform it first
    if (pendingCalculation) {
      performCalculation();
    }
    
    if (waitingForNewValue) {
      // Starting a new number after an operation or parenthesis
      setDisplay(num);
      setWaitingForNewValue(false);
      if (fullExpression) {
        // Check if last character is a parenthesis
        const trimmed = fullExpression.trim();
        const lastChar = trimmed.slice(-1);
        if (lastChar === '(' || lastChar === ')') {
          // Append the number directly (no space after parenthesis)
          const expr = `${fullExpression}${num}`;
          setCurrentExpression(expr);
          setFullExpression(expr);
        } else {
          // Append the number with space
          const expr = `${fullExpression} ${num}`;
          setCurrentExpression(expr);
          setFullExpression(expr);
        }
      } else if (previousValue !== null && operation !== null) {
        const expr = `${previousValue} ${getOperationSymbol(operation)} ${num}`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
    } else {
      // Continuing to type the current number
      const newDisplay = display === '0' ? num : display + num;
      setDisplay(newDisplay);
      if (fullExpression) {
        // Update the last number in the expression, preserving parentheses
        // Use regex to find the last number (not inside parentheses) and replace it
        let updatedExpr = fullExpression;
        
        // Find the last number that's not part of a parenthesized expression
        // This is tricky - we'll look for the pattern: space + number at the end
        // or number at the end of a parenthesized group
        const lastNumberMatch = updatedExpr.match(/(\s+)(\d+(\.\d+)?)(\s*)$/);
        if (lastNumberMatch) {
          // Replace the last number
          updatedExpr = updatedExpr.substring(0, lastNumberMatch.index) + lastNumberMatch[1] + newDisplay + lastNumberMatch[4];
        } else {
          // Check if expression ends with a number directly (no space)
          const directNumberMatch = updatedExpr.match(/(\d+(\.\d+)?)$/);
          if (directNumberMatch) {
            updatedExpr = updatedExpr.substring(0, directNumberMatch.index) + newDisplay;
          } else {
            // Check if last character is a parenthesis or operator
            const trimmed = updatedExpr.trim();
            const lastChar = trimmed.slice(-1);
            if (lastChar === '(' || lastChar === ')' || lastChar === '+' || lastChar === '−' || lastChar === '-' || 
                lastChar === '×' || lastChar === '*' || lastChar === '÷' || lastChar === '/') {
              // Append the number
              updatedExpr = `${updatedExpr} ${newDisplay}`;
            } else {
              // Try to append
              updatedExpr = `${updatedExpr} ${newDisplay}`;
            }
          }
        }
        
        setCurrentExpression(updatedExpr);
        setFullExpression(updatedExpr);
      } else if (previousValue !== null && operation !== null) {
        const expr = `${previousValue} ${getOperationSymbol(operation)} ${newDisplay}`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
    }
  };

  const handleOpenParenthesis = () => {
    // If there's a pending calculation, perform it first
    if (pendingCalculation) {
      performCalculation();
    }
    
    if (fullExpression) {
      // Check if we need to add an operation before the parenthesis
      const lastChar = fullExpression.trim().slice(-1);
      if (lastChar !== '+' && lastChar !== '−' && lastChar !== '-' && lastChar !== '×' && lastChar !== '*' && lastChar !== '÷' && lastChar !== '/' && lastChar !== '(') {
        // Need to add multiplication (implicit multiplication)
        const expr = `${fullExpression} × (`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      } else {
        // Add opening parenthesis to the expression
        const expr = `${fullExpression} (`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
      setDisplay('0');
      setWaitingForNewValue(true);
      setPreviousValue(null);
      setOperation(null);
    } else {
      // Start new expression with opening parenthesis
      const expr = '(';
      setCurrentExpression(expr);
      setFullExpression(expr);
      setDisplay('0');
      setWaitingForNewValue(true);
      setPreviousValue(null);
      setOperation(null);
    }
  };

  const handleCloseParenthesis = () => {
    if (fullExpression) {
      // Add closing parenthesis to the expression - just record it, don't evaluate
      const expr = `${fullExpression} )`;
      setCurrentExpression(expr);
      setFullExpression(expr);
      // Don't evaluate - just record the closing parenthesis
    }
  };

  const handleDecimal = () => {
    // If there's a pending calculation, perform it first
    if (pendingCalculation) {
      performCalculation();
    }
    
    if (waitingForNewValue) {
      setDisplay('0.');
      setWaitingForNewValue(false);
      if (fullExpression) {
        const expr = `${fullExpression} 0.`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      } else if (previousValue !== null && operation !== null) {
        const expr = `${previousValue} ${getOperationSymbol(operation)} 0.`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
    } else if (display.indexOf('.') === -1) {
      const newDisplay = display + '.';
      setDisplay(newDisplay);
      if (fullExpression) {
        const parts = fullExpression.split(' ');
        if (parts.length >= 3) {
          parts[parts.length - 1] = newDisplay;
          const expr = parts.join(' ');
          setCurrentExpression(expr);
          setFullExpression(expr);
        } else if (parts.length === 2) {
          const expr = `${fullExpression} ${newDisplay}`;
          setCurrentExpression(expr);
          setFullExpression(expr);
        }
      } else if (previousValue !== null && operation !== null) {
        const expr = `${previousValue} ${getOperationSymbol(operation)} ${newDisplay}`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
    }
  };

  const getOperationSymbol = (op: string): string => {
    switch (op) {
      case '+': return '+';
      case '-': return '−';
      case '*': return '×';
      case '/': return '÷';
      default: return op;
    }
  };

  const handleOperation = (op: string) => {
    // If there's a pending calculation, perform it first
    if (pendingCalculation) {
      performCalculation();
    }
    
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      // First operation - check if we have a fullExpression (e.g., from parentheses)
      if (fullExpression) {
        // Update the last number in fullExpression with current input
        const trimmed = fullExpression.trim();
        if (trimmed.endsWith(')')) {
          // Expression ends with closing parenthesis - just append the operation
          const expr = `${fullExpression} ${getOperationSymbol(op)}`;
          setCurrentExpression(expr);
          setFullExpression(expr);
        } else {
          // Update the last number in fullExpression
          const lastNumberRegex = /(\s+)(\d+(\.\d+)?)(\s*)$/;
          const match = fullExpression.match(lastNumberRegex);
          
          if (match) {
            // Replace the last number
            const updatedExpr = fullExpression.substring(0, match.index) + match[1] + inputValue.toString() + match[4];
            const expr = `${updatedExpr} ${getOperationSymbol(op)}`;
            setCurrentExpression(expr);
            setFullExpression(expr);
          } else {
            // Check if it ends with a number directly (no trailing space)
            const directNumberRegex = /(\d+(\.\d+)?)$/;
            const directMatch = fullExpression.match(directNumberRegex);
            if (directMatch) {
              const updatedExpr = fullExpression.substring(0, directMatch.index) + inputValue.toString();
              const expr = `${updatedExpr} ${getOperationSymbol(op)}`;
              setCurrentExpression(expr);
              setFullExpression(expr);
            } else {
              // No number at the end - append it with operation
              const expr = `${fullExpression} ${inputValue} ${getOperationSymbol(op)}`;
              setCurrentExpression(expr);
              setFullExpression(expr);
            }
          }
        }
        setPreviousValue(inputValue);
      } else {
        // No fullExpression - create new expression
        setPreviousValue(inputValue);
        const expr = `${inputValue} ${getOperationSymbol(op)}`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
    } else if (operation) {
      // Chaining operations - just update the expression, don't evaluate
      let updatedFullExpr: string;
      
      if (fullExpression) {
        // Check if the expression ends with a closing parenthesis
        const trimmed = fullExpression.trim();
        if (trimmed.endsWith(')')) {
          // Expression ends with closing parenthesis - just append the operation
          updatedFullExpr = fullExpression;
        } else {
          // Update the last number in fullExpression with the current input
          // Use regex to find and replace the last number, preserving structure
          const lastNumberRegex = /(\s+)(\d+(\.\d+)?)(\s*)$/;
          const match = fullExpression.match(lastNumberRegex);
          
          if (match) {
            // Replace the last number
            updatedFullExpr = fullExpression.substring(0, match.index) + match[1] + inputValue.toString() + match[4];
          } else {
            // Check if it ends with a number directly (no trailing space)
            const directNumberRegex = /(\d+(\.\d+)?)$/;
            const directMatch = fullExpression.match(directNumberRegex);
            if (directMatch) {
              updatedFullExpr = fullExpression.substring(0, directMatch.index) + inputValue.toString();
            } else {
              // No number at the end - append it
              updatedFullExpr = `${fullExpression} ${inputValue}`;
            }
          }
        }
      } else {
        // Fallback: construct from current state
        updatedFullExpr = `${previousValue} ${getOperationSymbol(operation)} ${inputValue}`;
      }
      
      // Just append the new operation - don't evaluate yet
      const newExpr = `${updatedFullExpr} ${getOperationSymbol(op)}`;
      setCurrentExpression(newExpr);
      setFullExpression(newExpr);
      
      // Keep the display showing the current input value (not evaluated result)
      setPreviousValue(inputValue);
    } else {
      // Operation without previous operation (shouldn't happen, but handle it)
      const expr = `${previousValue} ${getOperationSymbol(op)}`;
      setCurrentExpression(expr);
      setFullExpression(expr);
    }

    setWaitingForNewValue(true);
    setOperation(op);
  };

  // Helper function to evaluate expression without parentheses (just operators)
  const evaluateWithoutParentheses = (tokens: (number | string)[]): number => {
    if (tokens.length === 0) return 0;
    if (tokens.length === 1) return typeof tokens[0] === 'number' ? tokens[0] : 0;

    // First pass: evaluate multiplication and division (left to right)
    const afterMulDiv: (number | string)[] = [];
    let i = 0;
    while (i < tokens.length) {
      if (typeof tokens[i] === 'number') {
        afterMulDiv.push(tokens[i]);
        i++;
      } else {
        const op = tokens[i] as string;
        if (op === '×' || op === '*' || op === '÷' || op === '/') {
          // This is multiplication or division - evaluate immediately
          const left = afterMulDiv.pop() as number;
          i++; // Move to next token (the right operand)
          const right = tokens[i] as number;
          if (op === '×' || op === '*') {
            afterMulDiv.push(left * right);
          } else {
            afterMulDiv.push(left / right);
          }
          i++; // Move past the right operand
        } else {
          // This is addition or subtraction - keep for second pass
          afterMulDiv.push(op);
          i++;
        }
      }
    }

    // Second pass: evaluate addition and subtraction (left to right)
    if (afterMulDiv.length === 0) return 0;
    let result = afterMulDiv[0] as number;
    for (let i = 1; i < afterMulDiv.length; i += 2) {
      const op = afterMulDiv[i] as string;
      const right = afterMulDiv[i + 1] as number;
      if (op === '+' || op === '−' || op === '-') {
        if (op === '−' || op === '-') {
          result -= right;
        } else {
          result += right;
        }
      }
    }

    return result;
  };

  // Evaluate expression with proper operator precedence and parentheses
  const evaluateExpression = (expression: string): number => {
    // First, handle parentheses by finding and evaluating innermost parentheses first
    let expr = expression.trim();
    
    // Find and evaluate innermost parentheses
    while (expr.includes('(') || expr.includes(')')) {
      const openIndex = expr.lastIndexOf('(');
      if (openIndex === -1) break;
      
      const closeIndex = expr.indexOf(')', openIndex);
      if (closeIndex === -1) break; // Unmatched parenthesis
      
      // Extract the expression inside parentheses
      const innerExpr = expr.substring(openIndex + 1, closeIndex).trim();
      
      // Evaluate the inner expression
      const innerResult = evaluateExpression(innerExpr);
      
      // Replace the parentheses expression with the result
      // Need to handle spacing - check if there's a space before and after
      const before = openIndex > 0 && expr[openIndex - 1] === ' ' ? ' ' : '';
      const after = closeIndex < expr.length - 1 && expr[closeIndex + 1] === ' ' ? ' ' : '';
      expr = expr.substring(0, openIndex) + before + innerResult.toString() + after + expr.substring(closeIndex + 1);
    }
    
    // Now parse the expression without parentheses into tokens
    const tokens: (number | string)[] = [];
    const parts = expr.trim().split(' ').filter(p => p.length > 0);
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part === '+' || part === '−' || part === '-' || part === '×' || part === '*' || part === '÷' || part === '/') {
        tokens.push(part);
      } else {
        const num = parseFloat(part);
        if (!isNaN(num)) {
          tokens.push(num);
        }
      }
    }

    // Evaluate the expression without parentheses
    return evaluateWithoutParentheses(tokens);
  };


  // Helper function to perform calculation and add to history
  const performCalculation = (): number => {
    // If there's a pending calculation, just add it to history and return
    if (pendingCalculation) {
      setHistory((prev) => [{ expression: pendingCalculation.expression, result: pendingCalculation.result }, ...prev]);
      setPendingCalculation(null);
      return pendingCalculation.result;
    }
    
    // Otherwise, perform the calculation normally
    const inputValue = parseFloat(display);
    
    // Build the complete expression to evaluate
    let expression: string;
    let result: number;
    
    if (fullExpression) {
      // Update the last number in fullExpression with current input
      const trimmed = fullExpression.trim();
      if (trimmed.endsWith(')')) {
        // Expression ends with closing parenthesis - it's already complete
        expression = fullExpression;
      } else {
        // Update the last number
        const lastNumberRegex = /(\s+)(\d+(\.\d+)?)(\s*)$/;
        const match = fullExpression.match(lastNumberRegex);
        
        if (match) {
          expression = fullExpression.substring(0, match.index) + match[1] + inputValue.toString() + match[4];
        } else {
          const directNumberRegex = /(\d+(\.\d+)?)$/;
          const directMatch = fullExpression.match(directNumberRegex);
          if (directMatch) {
            expression = fullExpression.substring(0, directMatch.index) + inputValue.toString();
          } else {
            expression = `${fullExpression} ${inputValue}`;
          }
        }
      }
      
      // Evaluate the full expression with proper operator precedence and parentheses
      result = evaluateExpression(expression);
      expression = `${expression} = ${result}`;
    } else if (previousValue !== null && operation !== null) {
      // Simple two-operand calculation
      expression = `${previousValue} ${getOperationSymbol(operation)} ${inputValue}`;
      result = evaluateExpression(expression);
      expression = `${expression} = ${result}`;
    } else {
      // No operation - just the current value
      result = inputValue;
      expression = `${inputValue} = ${result}`;
    }
    
    // Add to history
    setHistory((prev) => [{ expression, result }, ...prev]);
    setDisplay(String(result));
    setCurrentExpression('');
    setFullExpression('');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(true);
    
    return result;
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);
    
    // Build the complete expression to evaluate (but don't calculate yet)
    let expression: string;
    let result: number;
    
    if (fullExpression) {
      // Update the last number in fullExpression with current input
      const trimmed = fullExpression.trim();
      if (trimmed.endsWith(')')) {
        // Expression ends with closing parenthesis - it's already complete
        expression = fullExpression;
      } else {
        // Update the last number
        const lastNumberRegex = /(\s+)(\d+(\.\d+)?)(\s*)$/;
        const match = fullExpression.match(lastNumberRegex);
        
        if (match) {
          expression = fullExpression.substring(0, match.index) + match[1] + inputValue.toString() + match[4];
        } else {
          const directNumberRegex = /(\d+(\.\d+)?)$/;
          const directMatch = fullExpression.match(directNumberRegex);
          if (directMatch) {
            expression = fullExpression.substring(0, directMatch.index) + inputValue.toString();
          } else {
            expression = `${fullExpression} ${inputValue}`;
          }
        }
      }
      
      // Evaluate the full expression with proper operator precedence and parentheses
      result = evaluateExpression(expression);
      expression = `${expression} = ${result}`;
    } else if (previousValue !== null && operation !== null) {
      // Simple two-operand calculation
      expression = `${previousValue} ${getOperationSymbol(operation)} ${inputValue}`;
      result = evaluateExpression(expression);
      expression = `${expression} = ${result}`;
    } else {
      // No operation - just the current value
      result = inputValue;
      expression = `${inputValue} = ${result}`;
    }
    
    // Store the pending calculation - don't perform it or add to history yet
    setPendingCalculation({ expression, result });
    setDisplay(String(result));
    setCurrentExpression('');
    setFullExpression('');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(true);
  };

  const handleClear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(false);
    setCurrentExpression('');
    setFullExpression('');
    setPendingCalculation(null);
  };

  const handleHistoryClick = (entry: HistoryEntry) => {
    setDisplay(entry.result.toString());
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(false);
    setCurrentExpression('');
    setFullExpression('');
    setPendingCalculation(null);
  };

  const clearHistory = () => {
    setHistory([]);
  };

  const handleBackspace = () => {
    if (display.length > 1) {
      const newDisplay = display.slice(0, -1);
      setDisplay(newDisplay);
      if (fullExpression) {
        const parts = fullExpression.split(' ');
        if (parts.length >= 3) {
          // Has format: "num op num" - update last number
          parts[parts.length - 1] = newDisplay;
          const expr = parts.join(' ');
          setCurrentExpression(expr);
          setFullExpression(expr);
        } else if (parts.length === 2) {
          // Has format: "num op" - append the updated number
          const expr = `${fullExpression} ${newDisplay}`;
          setCurrentExpression(expr);
          setFullExpression(expr);
        }
      } else if (previousValue !== null && operation !== null) {
        const expr = `${previousValue} ${getOperationSymbol(operation)} ${newDisplay}`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
    } else {
      setDisplay('0');
      if (fullExpression) {
        const parts = fullExpression.split(' ');
        if (parts.length >= 3) {
          parts[parts.length - 1] = '0';
          const expr = parts.join(' ');
          setCurrentExpression(expr);
          setFullExpression(expr);
        } else if (parts.length === 2) {
          const expr = `${fullExpression} 0`;
          setCurrentExpression(expr);
          setFullExpression(expr);
        }
      } else if (previousValue !== null && operation !== null) {
        const expr = `${previousValue} ${getOperationSymbol(operation)} 0`;
        setCurrentExpression(expr);
        setFullExpression(expr);
      }
    }
  };

  const handleApply = () => {
    // Calculate first (handles pending calculations or incomplete expressions)
    const result = performCalculation();
    onCalculate(result);
    onClose();
  };

  // Update refs when handlers are available
  useEffect(() => {
    handlersRef.current = {
      handleNumber,
      handleDecimal,
      handleOperation,
      handleEquals,
      handleClear,
      handleBackspace,
      handleOpenParenthesis,
      handleCloseParenthesis,
    };
  });

  // Keyboard event handler
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent default for calculator keys to avoid unwanted behavior
      const key = event.key;
      const handlers = handlersRef.current;
      if (!handlers) return;
      
      // Number keys (0-9)
      if (key >= '0' && key <= '9') {
        event.preventDefault();
        handlers.handleNumber(key);
        return;
      }

      // Decimal point
      if (key === '.' || key === ',') {
        event.preventDefault();
        handlers.handleDecimal();
        return;
      }

      // Operations
      if (key === '+') {
        event.preventDefault();
        handlers.handleOperation('+');
        return;
      }
      if (key === '-') {
        event.preventDefault();
        handlers.handleOperation('-');
        return;
      }
      if (key === '*') {
        event.preventDefault();
        handlers.handleOperation('*');
        return;
      }
      if (key === '/') {
        event.preventDefault();
        handlers.handleOperation('/');
        return;
      }

      // Equals (Enter or = key)
      if (key === 'Enter' || key === '=') {
        event.preventDefault();
        handlers.handleEquals();
        return;
      }

      // Clear (Escape or Delete)
      if (key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (key === 'Delete') {
        event.preventDefault();
        handlers.handleClear();
        return;
      }

      // Backspace
      if (key === 'Backspace') {
        event.preventDefault();
        handlers.handleBackspace();
        return;
      }

      // Parentheses
      if (key === '(') {
        event.preventDefault();
        handlers.handleOpenParenthesis();
        return;
      }
      if (key === ')') {
        event.preventDefault();
        handlers.handleCloseParenthesis();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  const buttonStyle = {
    minWidth: '60px',
    height: '60px',
    fontSize: '1.2rem',
    fontWeight: 500,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">Calculator</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Current Expression */}
          {currentExpression && (
            <Box
              sx={{
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                textAlign: 'right',
                minHeight: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {currentExpression}
              </Typography>
            </Box>
          )}
          
          {/* Display */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'action.selected',
              borderRadius: 1,
              textAlign: 'right',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography variant="h4" sx={{ wordBreak: 'break-all' }}>
              {display}
            </Typography>
          </Box>

          {/* History Section */}
          {history.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                maxHeight: '200px',
                overflow: 'auto',
                bgcolor: 'action.hover',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  History
                </Typography>
                <Button size="small" onClick={clearHistory} sx={{ minWidth: 'auto', px: 1 }}>
                  Clear
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {history.map((entry, index) => (
                  <Box
                    key={index}
                    onClick={() => handleHistoryClick(entry)}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {entry.expression}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
          {/* Calculator Buttons - 4 Column Layout (Operations on Right) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {/* Row 1: Clear, Backspace, Parentheses */}
            <Button variant="outlined" onClick={handleClear} sx={buttonStyle} color="error">
              C
            </Button>
            <Button variant="outlined" onClick={handleBackspace} sx={buttonStyle}>
              ⌫
            </Button>
            <Button variant="outlined" onClick={handleOpenParenthesis} sx={buttonStyle}>
              (
            </Button>
            <Button variant="outlined" onClick={handleCloseParenthesis} sx={buttonStyle}>
              )
            </Button>
            
            {/* Row 2: 7, 8, 9, Division */}
            <Button variant="contained" onClick={() => handleNumber('7')} sx={buttonStyle}>
              7
            </Button>
            <Button variant="contained" onClick={() => handleNumber('8')} sx={buttonStyle}>
              8
            </Button>
            <Button variant="contained" onClick={() => handleNumber('9')} sx={buttonStyle}>
              9
            </Button>
            <Button variant="outlined" onClick={() => handleOperation('/')} sx={buttonStyle}>
              ÷
            </Button>
            
            {/* Row 3: 4, 5, 6, Multiplication */}
            <Button variant="contained" onClick={() => handleNumber('4')} sx={buttonStyle}>
              4
            </Button>
            <Button variant="contained" onClick={() => handleNumber('5')} sx={buttonStyle}>
              5
            </Button>
            <Button variant="contained" onClick={() => handleNumber('6')} sx={buttonStyle}>
              6
            </Button>
            <Button variant="outlined" onClick={() => handleOperation('*')} sx={buttonStyle}>
              ×
            </Button>
            
            {/* Row 4: 1, 2, 3, Subtraction */}
            <Button variant="contained" onClick={() => handleNumber('1')} sx={buttonStyle}>
              1
            </Button>
            <Button variant="contained" onClick={() => handleNumber('2')} sx={buttonStyle}>
              2
            </Button>
            <Button variant="contained" onClick={() => handleNumber('3')} sx={buttonStyle}>
              3
            </Button>
            <Button variant="outlined" onClick={() => handleOperation('-')} sx={buttonStyle}>
              −
            </Button>
            
            {/* Row 5: 0 (span 2), Decimal, Addition, Equals (span 2 rows) */}
            <Button
              variant="contained"
              onClick={() => handleNumber('0')}
              sx={buttonStyle}
            >
              0
            </Button>
            <Button variant="contained" onClick={handleDecimal} sx={buttonStyle}>
              .
            </Button>
            <Button
              variant="contained"
              onClick={handleEquals}
              sx={buttonStyle}
              color="primary"
            >
              =
            </Button>
            <Button variant="outlined" onClick={() => handleOperation('+')} sx={buttonStyle}>
              +
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
